// WWI messenger pigeon loft — the signals corps' bird post. A tall timber loft
// raised on four posts, its front face a grid of dark pigeon holes over a
// landing ledge, capped by a small terracotta gable; a leaning ladder and two
// white pigeons (one on the ledge, one on the ridge) sell the subject from the
// 45-degree iso view. The dispatch spot is on open ground before the ledge.
import * as THREE from "three";
import { defineModel, standVisit, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, ROOF_HOME } = styleV3;

// Muted WWI materials.
const TIMBER_W = "#8b6438";
const MUD = "#5d4a33";
const GUNMETAL = "#4a4d52";
const FEATHER = "#e9e6de";

const BASE = 0.12;            // foundation slab top
const POST_H = 0.45;          // stilt height above the slab
const LOFT_W = 0.56;
const LOFT_H = 0.42;
const LOFT_D = 0.44;
const LOFT_Y = BASE + POST_H; // loft floor
const FRONT = LOFT_D / 2;     // the +z pigeon-hole face

// The four stilts the loft stands on.
function stilts(h: number): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-0.22, -0.16], [0.22, -0.16], [-0.22, 0.16], [0.22, 0.16]] as const) {
    out.push(cyl(0.03, 0.035, h, 5, TIMBER_W, [x, BASE + h / 2, z]));
  }
  return out;
}

// The loft body on top of the stilts.
function loft(): Part {
  return box(LOFT_W, LOFT_H, LOFT_D, TIMBER_W, [0, LOFT_Y + LOFT_H / 2, 0]);
}

// The grid of dark pigeon holes on the front face, plus the landing ledge.
function holesAndLedge(): Part[] {
  const parts: Part[] = [];
  for (const y of [LOFT_Y + 0.13, LOFT_Y + 0.28]) {
    for (const x of [-0.17, 0, 0.17]) {
      parts.push(box(0.09, 0.09, 0.03, MUD, [x, y, FRONT + 0.01]));
    }
  }
  parts.push(box(0.52, 0.03, 0.12, "#a3814f", [0, LOFT_Y + 0.02, FRONT + 0.07]));
  return parts;
}

// A white pigeon: squat sphere body + tiny head.
function pigeon(x: number, y: number, z: number, yaw: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.045, 7, 5), color: FEATHER,
      position: [x, y, z], rotation: [0, yaw, 0], scale: [0.9, 0.8, 1.2] },
    { geometry: new THREE.SphereGeometry(0.024, 6, 5), color: FEATHER,
      position: [x + Math.sin(yaw) * 0.05, y + 0.045, z + Math.cos(yaw) * 0.05] },
  ];
}

// The access ladder leaning up to the landing ledge on the front.
function ladder(): Part[] {
  const lean: [number, number, number] = [0.32, 0, 0];
  const parts: Part[] = [
    box(0.03, 0.6, 0.03, TIMBER_W, [0.19, BASE + 0.29, 0.335], lean),
    box(0.03, 0.6, 0.03, TIMBER_W, [0.31, BASE + 0.29, 0.335], lean),
  ];
  // Rungs step back in z to follow the lean.
  for (const [y, z] of [[0.24, 0.391], [0.39, 0.342], [0.54, 0.292]] as const) {
    parts.push(box(0.15, 0.03, 0.03, MUD, [0.25, y, z]));
  }
  return parts;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.86, 0.86)];
  if (s === 1) return [slab(0.86, 0.86), ...stilts(POST_H), ...poles(0.8, 0.76, 0.7)];
  if (s === 2) return [slab(0.86, 0.86), ...stilts(POST_H), loft(), ...poles(0.8, 0.76, 1.15)];

  const eave = LOFT_Y + LOFT_H;       // top of the loft body
  return [
    slab(0.86, 0.86),
    ...stilts(POST_H),
    loft(),
    // Small pitched terracotta roof — the civic accent, ridge along x. ONE part.
    gable(0.68, 0.56, 0.26, ROOF_HOME, 0, eave, 0, "x"),
    ...holesAndLedge(),
    ...ladder(),
    // A message tube hanging under the loft floor.
    cyl(0.03, 0.03, 0.1, 6, GUNMETAL, [-0.18, LOFT_Y - 0.07, 0.1]),
    // Two white pigeons: one on the ledge, one on the ridge.
    ...pigeon(0.13, LOFT_Y + 0.075, FRONT + 0.07, 0),
    ...pigeon(-0.1, eave + 0.3, 0, Math.PI / 2),
  ];
}

export const pigeonLoft = defineModel({
  id: "pigeon-loft",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Messenger Pigeon Loft",
  tags: ["building", "wwi", "military", "pigeon", "signals", "communication"],
  summary: "A raised timber loft where messenger pigeons roost and dispatch.",
  description:
    "A tall timber pigeon loft on stilts, its face a grid of nesting holes over " +
    "a landing ledge under a small terracotta gable. A signaller visits the " +
    "ledge to dispatch a bird with a message tube.",
  author: "Fable 5",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => partsFor(0) },
    { id: "lower", label: "Lower", parts: () => partsFor(1) },
    { id: "upper", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "dispatch", position: [0, 0.12, 0.5], facing: Math.PI, description: "Where the signaller dispatches a bird." },
  ],
  interactions: [
    { id: "dispatch-bird", label: "Dispatching a pigeon", station: "dispatch", variants: ["complete"], ...standVisit() },
  ],
});
